import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import WebsocketContext from './WebsocketContext.ts';
import type { HubConnection } from '@microsoft/signalr';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getUrlFromSwaggerUrl } from 'app/apis/api.ts';
import { useMsal } from '@azure/msal-react';
import { apiLoginRequest } from '@/authConfig.ts';
import logger, { getErrorForLog, getSafeErrorLog } from 'app/utils/logger.ts';

const getWebsocketUrl = (): string => `${getUrlFromSwaggerUrl(process.env.REACT_APP_SCENES)}/hubs/training` || '';

interface WebsocketProviderProps {
  children: ReactNode;
}

const WebsocketProvider: React.FC<WebsocketProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();

  // Cache for WebSocket tokens to prevent excessive refreshes
  const tokenCacheRef = useRef<{ token: string; expiresAt: number } | null>(null);
  const TOKEN_BUFFER_TIME = 5 * 60 * 1000; // 5 minutes buffer

  const [connection] = useState<HubConnection>(() => {
    // Create token factory function inside useState to avoid dependency issues
    const getWebSocketToken = async (): Promise<string> => {
      const now = Date.now();

      // Check if we have a valid cached token
      if (tokenCacheRef.current && now < tokenCacheRef.current.expiresAt - TOKEN_BUFFER_TIME) {
        return tokenCacheRef.current.token;
      }

      // Get new token
      const response = await instance.acquireTokenSilent({
        ...apiLoginRequest,
        account: accounts[0],
        forceRefresh: false // Use cached tokens when possible
      });

      // Cache the token
      tokenCacheRef.current = {
        token: response.accessToken,
        expiresAt: response.expiresOn?.getTime() || now + 60 * 60 * 1000 // Default 1 hour
      };

      return response.accessToken;
    };

    const connection = new HubConnectionBuilder()
      .configureLogging(LogLevel.Debug)
      .withUrl(getWebsocketUrl(), {
        transport: HttpTransportType.ServerSentEvents,
        accessTokenFactory: getWebSocketToken
      })
      .withAutomaticReconnect()
      .build();

    connection.keepAliveIntervalInMilliseconds = 1000 * 15; // 15 seconds
    connection.serverTimeoutInMilliseconds = 1000 * 60 * 360; // 360  minutes

    return connection;
  });

  const startConnection = useCallback(async () => {
    try {
      await connection.start();
      logger.log('Websocket successfully opened!');
    } catch (e) {
      logger.error(getErrorForLog(e, 'Websocket initialization failed'), {
        context: 'Websocket initialization failed',
        error: getSafeErrorLog(e)
      });
    }
  }, [connection]);

  const stopConnection = useCallback(async () => {
    await connection.stop();
  }, [connection]);

  const subscribe = useCallback(
    (methodName: string, newMethod: (...args: any[]) => void) => {
      connection.on(methodName, newMethod);
    },
    [connection]
  );

  const value = useMemo(() => {
    return {
      connection: connection,
      startConnection,
      stopConnection,
      subscribe
    };
  }, [subscribe, stopConnection, startConnection, connection]);

  return <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>;
};

export default WebsocketProvider;
